import { FC, Fragment, useState, MouseEvent } from 'react'

import { useAuth } from 'oidc-react'

import { Box, Popover, Typography, IconButton, Button, Chip, Switch, FormControlLabel, CircularProgress } from '@mui/material'

import AccountIcon from '@mui/icons-material/AccountCircle'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import LogoutIcon from '@mui/icons-material/Logout'
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined'
import { getEnvChipColors } from '../Header/envColor'
import { ScanIcon } from '../../wms/src/components/_WMS/_icons/ScanIcon'
import { useUIMode } from '../../wms/src/context/UIContext'
import { useSelector } from 'react-redux';
import axios from 'axios'
import { apiCall, ApiConfig } from '../../wms/src/config/api'


const PDF_MANUAL_FILENAME = 'manual_wms_infra.pdf'
const JIRA_URL = 'https://infracommerce.atlassian.net/servicedesk/customer/portal/38/group/1224/create/961'
const CONFLUENCE_URL = 'https://infracommerce.atlassian.net/wiki/spaces/WME/overview'

interface Props {

}

export const Profile: FC<Props> = () => {
    const auth = useAuth()
    const env = import.meta.env.VITE_ENV
    const { bg: envBg, color: envColor } = getEnvChipColors(env ?? '')
    const { isMobile, viewMode, toggleMode, resetMode, captureMode, setCaptureMode, scanBeep, setScanBeep } = useUIMode()
    const tenant = auth.userData?.profile.tenant as string | undefined;
    const { configurationState } = useSelector((state: any) => state.configuration);
    const configuration = configurationState?.configuration || [];
    const tenantNameConfig = configuration.find((c: any) => c.name === 'TenantName')?.value;
    const displayTenant = tenantNameConfig || auth.userData?.profile.tenant;

    const [anchorEl, setAnchorEl] = useState<Element | null>(null)
    const [downloadingPDF, setDownloadingPDF] = useState(false)

    const handleClick = (event: MouseEvent) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleLogout = () => {
        handleClose()
        resetMode()
        sessionStorage.removeItem('wms_selected_page_name')
        auth.signOutRedirect()
    }

    const handleDownloadPDF = () => {
        setDownloadingPDF(true)
        apiCall('GET', `${ApiConfig.ProcessApi}/GetTemplateFileUrl/${PDF_MANUAL_FILENAME}`)
            .then((response: any) => {
                if (response.status === 200 && response.data?.url) {
                    const link = document.createElement('a')
                    link.style.display = 'none'
                    link.href = response.data.url
                    link.setAttribute('download', response.data.name)
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                    URL.revokeObjectURL(link.href)
                }
            })
            .catch((error: any) => {
                console.error('Error al descargar el manual:', error)
            })
            .finally(() => {
                setDownloadingPDF(false)
            })
    }


    const name = auth.userData?.profile.name

    const sectionBox = {
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 1.5,
    }

    // Icono arriba del texto, a diferencia del toggle de visualización que es solo texto.
    // Compacto a propósito: al apilar ícono + texto, el alto por defecto del Button
    // hacía que esta tarjeta pesara más que las demás en un popover ya angosto.
    const captureModeButton = {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 0.1,
        borderRadius: 2,
        py: 0.6,
        minHeight: 0,
        textTransform: 'none' as const,
        fontWeight: 700,
        fontSize: '12px',
        lineHeight: 1.3,
    }

    return (
        <Fragment>
            <IconButton
                edge="end"
                aria-label="account of current user"
                aria-haspopup="true"
                color="inherit"
                onClick={handleClick}
            >
                <AccountIcon sx={{ fontSize: 34 }} />
            </IconButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{
                    paper: {
                        sx: {
                            // En un celular de ~360px CSS el panel de 320px ocupa casi todo
                            // el ancho, asi que queda muy poco backdrop donde tocar para
                            // cerrar. El limite de alto evita ademas que "Cerrar sesion"
                            // quede fuera de pantalla: el contenido scrollea adentro.
                            maxHeight: '85vh',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }
                }}
            >
                <Box sx={{ p: 2, width: 320, display: 'flex', flexDirection: 'column', gap: 1.3, overflowY: 'auto' }}>

                    {/* NUEVO: Switch para cambiar de modo, solo visible en Mobile */}
                    {isMobile && (
                        <Box sx={{ ...sectionBox, p: 2 }}>
                            <Typography variant="caption" sx={{ color: '#8898aa', fontWeight: 700, letterSpacing: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>
                                MODO DE VISUALIZACIÓN
                            </Typography>
                            
                            <Box 
                                sx={{ 
                                    display: 'flex', 
                                    mt: 1.5, 
                                    backgroundColor: '#f1f3f6', // Fondo gris claro del contenedor
                                    borderRadius: 2.5, 
                                    p: 0.5 
                                }}
                            >
                                <Button
                                    fullWidth
                                    disableElevation
                                    variant={viewMode === 'short' ? 'contained' : 'text'}
                                    onClick={() => { if 
                                        (viewMode !== 'short') toggleMode()
                                        handleClose()
                                     }}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        color: viewMode === 'short' ? '#fff' : '#6c757d',
                                        backgroundColor: viewMode === 'short' ? '#0b47a8' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: viewMode === 'short' ? '#083682' : 'rgba(0,0,0,0.04)',
                                        }
                                    }}
                                >
                                    Vista RF
                                </Button>
                                
                                <Button
                                    fullWidth
                                    disableElevation
                                    variant={viewMode === 'full' ? 'contained' : 'text'}
                                    onClick={() => { 
                                        if (viewMode !== 'full') toggleMode()
                                        handleClose() 
                                        }}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        color: viewMode === 'full' ? '#fff' : '#6c757d',
                                        backgroundColor: viewMode === 'full' ? '#0b47a8' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: viewMode === 'full' ? '#083682' : 'rgba(0,0,0,0.04)',
                                        }
                                    }}
                                >
                                    Vista total
                                </Button>
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ ...sectionBox, p: 1.2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.8, fontSize: '11px' }}>
                            USUARIO
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{name}</Typography>
                    </Box>

                    <Box sx={{ ...sectionBox, p: 1.2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.8, fontSize: '11px' }}>
                            AMBIENTE
                        </Typography>
                        <Box sx={{ mt: 0.4 }}>
                            <Chip
                                label={env?.toUpperCase() || 'PRODUCCIÓN'}
                                size="small"
                                sx={{ backgroundColor: envBg, color: envColor, fontWeight: 700, fontSize: '12px' }}
                            />
                        </Box>
                    </Box>

                    {tenant && (
                        <Box sx={{ ...sectionBox, p: 1.2 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.8, fontSize: '11px' }}>
                                TENANT
                            </Typography>
                            <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{displayTenant}</Typography>
                        </Box>
                    )}

                    {/* Modo de captura: decide si se ofrece el escaneo por cámara.
                        Solo en mobile, porque en desktop la cámara no aplica. */}
                    {isMobile && (
                        <Box sx={{ ...sectionBox, p: 2 }}>
                            <Typography variant="caption" sx={{ color: '#8898aa', fontWeight: 700, letterSpacing: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>
                                MODO DE CAPTURA
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, backgroundColor: '#f1f3f6', borderRadius: 2.5, p: 0.5 }}>
                                <Button
                                    fullWidth
                                    disableElevation
                                    variant={captureMode === 'handheld' ? 'contained' : 'text'}
                                    onClick={() => { setCaptureMode('handheld'); handleClose() }}
                                    sx={{
                                        ...captureModeButton,
                                        color: captureMode === 'handheld' ? '#fff' : '#6c757d',
                                        backgroundColor: captureMode === 'handheld' ? '#0b47a8' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: captureMode === 'handheld' ? '#083682' : 'rgba(0,0,0,0.04)',
                                        }
                                    }}
                                >
                                    <ScanIcon width={20} height={14} sx={{ color: 'inherit' }} />
                                    Handheld
                                </Button>

                                <Button
                                    fullWidth
                                    disableElevation
                                    variant={captureMode === 'camera' ? 'contained' : 'text'}
                                    onClick={() => { setCaptureMode('camera'); handleClose() }}
                                    sx={{
                                        ...captureModeButton,
                                        color: captureMode === 'camera' ? '#fff' : '#6c757d',
                                        backgroundColor: captureMode === 'camera' ? '#0b47a8' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: captureMode === 'camera' ? '#083682' : 'rgba(0,0,0,0.04)',
                                        }
                                    }}
                                >
                                    <PhotoCameraOutlinedIcon sx={{ fontSize: 17 }} />
                                    Cámara
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Beep al escanear sin foco (lector o cámara). Solo mobile, igual
                        que el modo de captura. No cierra el popover: es un switch. */}
                    {isMobile && (
                        <Box sx={{ ...sectionBox, p: 2 }}>
                            <Typography variant="caption" sx={{ color: '#8898aa', fontWeight: 700, letterSpacing: 0.5, fontSize: '11px', textTransform: 'uppercase' }}>
                                SONIDO DE ESCANEO
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={scanBeep}
                                            onChange={(e) => setScanBeep(e.target.checked)}
                                            name="scanBeep"
                                        />
                                    }
                                    label={scanBeep ? 'Activado' : 'Desactivado'}
                                    sx={{ '& .MuiFormControlLabel-label': { fontWeight: 700, fontSize: '14px' } }}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ ...sectionBox, p: 1.2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.8, fontSize: '11px' }}>
                            AYUDA
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.8 }}>
                            <Button
                                variant="outlined"
                                onClick={() => window.open(CONFLUENCE_URL, '_blank')}
                                startIcon={<OpenInNewIcon />}
                                sx={{
                                    color: '#004bb1',
                                    borderColor: '#004bb1',
                                    borderRadius: 4,
                                    py: 0.9,
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    flex: 1,
                                    '&:hover': {
                                        backgroundColor: '#004bb1',
                                        color: 'white',
                                        borderColor: '#004bb1'
                                    }
                                }}
                            >
                                Confluence
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={handleDownloadPDF}
                                disabled={downloadingPDF}
                                startIcon={downloadingPDF ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
                                sx={{
                                    color: '#004bb1',
                                    borderColor: '#004bb1',
                                    borderRadius: 4,
                                    py: 0.9,
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    flex: 1,
                                    '&:hover': {
                                        backgroundColor: '#004bb1',
                                        color: 'white',
                                        borderColor: '#004bb1'
                                    }
                                }}
                            >
                                {downloadingPDF ? 'Descargando...' : 'Descargar'}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ ...sectionBox, p: 1.2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 0.8, fontSize: '11px' }}>
                            INCIDENTES
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.8 }}>
                            <Button
                                variant="outlined"
                                onClick={() => window.open(JIRA_URL, '_blank')}
                                startIcon={<SupportAgentIcon />}
                                sx={{
                                    color: '#004bb1',
                                    borderColor: '#004bb1',
                                    borderRadius: 4,
                                    py: 0.9,
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    flex: 1,
                                    '&:hover': {
                                        backgroundColor: '#004bb1',
                                        color: 'white',
                                        borderColor: '#004bb1'
                                    }
                                }}
                            >
                                Crear ticket
                            </Button>
                            <Box sx={{ flex: 1 }} />
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleLogout}
                        startIcon={<LogoutIcon />}
                        sx={{ borderRadius: 4, py: 0.9, textTransform: 'none', fontSize: '15px', mt: 3 }}
                    >
                        Cerrar sesión
                    </Button>

                </Box>
            </Popover>
        </Fragment>
    )
}
